import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import './ParticleNotificationBell.css';

const ParticleNotificationBell = ({ unreadCount, onClick }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const particlesRef = useRef([]);
  const animationFrameRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      75,
      1, // Square aspect ratio
      0.1,
      1000
    );
    camera.position.z = 150;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: true,
      powerPreference: "high-performance"
    });
    renderer.setSize(160, 160); // Reduced from 200x200 to keep particles more contained
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Limit for performance
    renderer.setClearColor(0x000000, 0);
    rendererRef.current = renderer;
    
    mountRef.current.appendChild(renderer.domElement);

    // Particle system setup
    const particleCount = Math.min(50 + (unreadCount * 10), 200); // Scale with notifications, max 200
    const particles = [];
    const connections = new THREE.Group();
    scene.add(connections);

    // IMT Brand Colors - IMT Greens and Amazon Orange
    const colors = [
      new THREE.Color(0x5a7b35), // IMT Mid Green
      new THREE.Color(0xffc20c), // IMT Gold Accent
      new THREE.Color(0x4a6b25), // IMT Primary Green
      new THREE.Color(0x3a5b15), // IMT Dark Green
      new THREE.Color(0xff9900), // Amazon Orange
      new THREE.Color(0xe68a00), // Amazon Orange Hover
    ];

    // Create particles with orbital properties
    for (let i = 0; i < particleCount; i++) {
      // Particle geometry - slightly bigger spheres
      const geometry = new THREE.SphereGeometry(1.2, 8, 8);
      
      // Random color from palette
      const color = colors[Math.floor(Math.random() * colors.length)];
      const material = new THREE.MeshBasicMaterial({ 
        color: color,
        transparent: true,
        opacity: 0.8
      });
      
      const particle = new THREE.Mesh(geometry, material);
      
      // Orbital properties - Smaller radius to stay closer to center
      const radius = 25 + Math.random() * 35; // Distance from center (reduced from 40-90 to 25-60)
      const theta = Math.random() * Math.PI * 2; // Angle
      const phi = Math.random() * Math.PI; // Vertical angle
      const speed = 0.001 + (unreadCount > 0 ? 0.002 : 0.0005) * Math.random(); // Faster if notifications
      const rotationSpeed = (Math.random() - 0.5) * 0.02;
      
      // Set initial position
      particle.position.x = radius * Math.sin(phi) * Math.cos(theta);
      particle.position.y = radius * Math.sin(phi) * Math.sin(theta);
      particle.position.z = radius * Math.cos(phi);
      
      // Store custom properties
      particle.userData = {
        radius,
        theta,
        phi,
        speed,
        rotationSpeed,
        baseRadius: radius,
        breathPhase: Math.random() * Math.PI * 2 // For breathing effect
      };
      
      scene.add(particle);
      particles.push(particle);
    }
    
    particlesRef.current = particles;

    // Add point light for depth
    const light = new THREE.PointLight(0xffc20c, 1, 200); // IMT Gold Accent
    light.position.set(0, 0, 50);
    scene.add(light);

    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(ambientLight);

    // Animation variables
    let breathTime = 0;
    const connectionDistance = 30; // Max distance to draw connections

    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      breathTime += 0.02;

      // Update particles
      particles.forEach((particle, index) => {
        const userData = particle.userData;
        
        // Update orbital position
        userData.theta += userData.speed;
        userData.phi += userData.rotationSpeed * 0.1;
        
        // Breathing effect - expand and contract
        const breathScale = 1 + Math.sin(breathTime + userData.breathPhase) * 0.15;
        const currentRadius = userData.baseRadius * breathScale;
        
        // Calculate new position
        particle.position.x = currentRadius * Math.sin(userData.phi) * Math.cos(userData.theta);
        particle.position.y = currentRadius * Math.sin(userData.phi) * Math.sin(userData.theta);
        particle.position.z = currentRadius * Math.cos(userData.phi);
        
        // Twinkling effect
        particle.material.opacity = 0.6 + Math.sin(breathTime * 2 + index * 0.5) * 0.2;
        
        // Scale particles slightly
        const scale = 1 + Math.sin(breathTime * 3 + index) * 0.3;
        particle.scale.setScalar(scale);
      });

      // Clear old connection lines
      connections.clear();

      // Draw connection lines between nearby particles (create molecular structure)
      if (unreadCount > 0) { // Only show connections when there are notifications
        const lineMaterial = new THREE.LineBasicMaterial({ 
          color: 0xff9900, // Amazon Orange for connection lines when notifications present
          transparent: true,
          opacity: 0.15
        });

        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < Math.min(i + 5, particles.length); j++) { // Limit connections for performance
            const distance = particles[i].position.distanceTo(particles[j].position);
            
            if (distance < connectionDistance) {
              const geometry = new THREE.BufferGeometry().setFromPoints([
                particles[i].position,
                particles[j].position
              ]);
              const line = new THREE.Line(geometry, lineMaterial);
              connections.add(line);
            }
          }
        }
      }

      // Rotate entire scene slowly
      scene.rotation.y += 0.001;
      scene.rotation.x = Math.sin(breathTime * 0.3) * 0.1;

      renderer.render(scene, camera);
    };

    animate();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      // Dispose of geometries and materials
      particles.forEach(particle => {
        particle.geometry.dispose();
        particle.material.dispose();
      });
      
      // Remove renderer
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [unreadCount]);

  // Handle particle intensity based on notification count
  useEffect(() => {
    if (particlesRef.current.length > 0) {
      const intensity = Math.min(1 + (unreadCount * 0.1), 2); // More notifications = faster/brighter
      
      particlesRef.current.forEach(particle => {
        // Increase speed based on notification count
        particle.userData.speed = 0.001 + (unreadCount > 0 ? 0.002 : 0.0005) * Math.random() * intensity;
      });
    }
  }, [unreadCount]);

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleClick(e);
    }
  };

  return (
    <div 
      className={`particle-notification-container ${isHovered ? 'hovered' : ''} ${unreadCount > 0 ? 'has-notifications' : ''}`}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onKeyPress={handleKeyPress}
      role="button"
      tabIndex={0}
      aria-label={`Notifications ${unreadCount > 0 ? `- ${unreadCount} unread` : ''}`}
    >
      {/* Glassmorphic base with hex grid pattern */}
      <div className="particle-base">
        <div className="hex-grid-pattern"></div>
      </div>

      {/* Three.js canvas container */}
      <div ref={mountRef} className="particle-canvas-container" />
      
      {/* Bell icon in center */}
      <div className="particle-bell-center">
        <FontAwesomeIcon icon={faBell} className="particle-bell-icon" />
      </div>

      {/* Notification badge */}
      {unreadCount > 0 && (
        <div className="particle-notification-badge">
          {unreadCount}
        </div>
      )}

      {/* Outer glow effect */}
      <div className="particle-outer-glow"></div>
    </div>
  );
};

export default ParticleNotificationBell;
